import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { withPanelBase } from "@/lib/authz";
import { toast } from "sonner";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  ScanBarcode,
  Printer,
  Package,
  MoreVertical,
  RefreshCw,
  Loader2,
  StickyNote,
  X,
  MapPin,
  Phone,
  Mail,
  User,
  CreditCard,
  Calendar,
  Hash,
  Truck,
  ClipboardList,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ─── Helpers ────────────────────────────────────────
function formatOrderDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  const day = String(d.getDate()).padStart(2, "0");
  const month = months[d.getMonth()];
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${hours}:${minutes}`;
}

function formatFullDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function normalizeExternalOrderNo(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  let s = raw;
  if (s.startsWith("WC-")) s = s.slice(3);
  if (s.startsWith("TY-")) s = s.slice(3);
  if (s.startsWith("HB-")) s = s.slice(3);
  if (s.startsWith("IDEFIX-")) s = s.slice(7);
  if (s.startsWith("PAZARAMA-")) s = s.slice(9);
  if (s.startsWith("AZ-")) s = s.slice(3);
  if (s.includes(" - ")) s = s.split(" - ", 1)[0].trim();
  if (s.startsWith("#")) s = s.slice(1);
  return s.trim();
}

function getTrendyolOrderNo(order) {
  return normalizeExternalOrderNo(order?.trendyol_order_number || "");
}

function getTrendyolPackageNo(order) {
  const explicit = normalizeExternalOrderNo(order?.trendyol_package_id || "");
  if (explicit) return explicit;
  const fromChannel = normalizeExternalOrderNo(order?.channel_order_id || "");
  if ((order?.channel_order_id || "").toString().startsWith("TY-")) return fromChannel;
  return "";
}

function getDisplayOrderNo(order) {
  const channel = String(order?.channel || "").toLowerCase();
  const tyOrderNo = getTrendyolOrderNo(order);
  const tyPackageNo = getTrendyolPackageNo(order);
  if (channel === "trendyol" || tyOrderNo || tyPackageNo) {
    if (tyOrderNo) return `#${tyOrderNo}`;
    if (tyPackageNo) return `#${tyPackageNo}`;
  }
  const generic = normalizeExternalOrderNo(order?.channel_order_id || "");
  return generic ? `#${generic}` : "-";
}

function getDisplayPackageNo(order) {
  const channel = String(order?.channel || "").toLowerCase();
  if (channel === "hepsiburada") {
    const pkg = normalizeExternalOrderNo(order?.hb_package_number || "");
    if (pkg) return pkg;
    const fromTracking = normalizeExternalOrderNo(order?.cargo_tracking || "");
    return fromTracking || "";
  }
  if (channel === "n11") {
    const orderNo = normalizeExternalOrderNo(order?.n11_order_number || "");
    if (orderNo) return orderNo;
    const tracking = normalizeExternalOrderNo(order?.n11_cargo_tracking || "");
    return tracking || "";
  }
  if (channel === "trendyol") {
    const pkg = getTrendyolPackageNo(order);
    const ord = getTrendyolOrderNo(order);
    if (pkg && pkg !== ord) return pkg;
  }
  if (channel === "idefix") {
    const tracking = normalizeExternalOrderNo(order?.idefix_cargo_tracking || order?.cargo_tracking || "");
    if (tracking) return tracking;
    const shipment = normalizeExternalOrderNo(order?.idefix_shipment_id || "");
    if (shipment) return shipment;
  }
  if (channel === "pazarama") {
    const tracking = normalizeExternalOrderNo(order?.pazarama_cargo_tracking || order?.cargo_tracking || "");
    if (tracking) return tracking;
    const orderNo = normalizeExternalOrderNo(order?.pazarama_order_number || "");
    return orderNo || "";
  }
  return "";
}

function getOrderChannel(order, fallbackProvider = "") {
  return String(order?.channel || fallbackProvider || "").trim().toLowerCase();
}

function isLocalCargoProvider(provider) {
  return ["hepsijet", "yurtici"].includes(String(provider || "").trim().toLowerCase());
}

function getOrderLocalBarcodes(order, fallbackProvider = "") {
  const existing = Array.isArray(order?.cargo_barcodes)
    ? order.cargo_barcodes.filter((item) => String(item?.barcode || "").trim())
    : [];
  if (existing.length > 0) return existing;

  const tracking = String(order?.cargo_tracking || "").trim();
  if (!tracking) return [];

  const channel = getOrderChannel(order, fallbackProvider);
  const cargoProvider = String(order?.cargo_provider || "").trim().toLowerCase();
  if (isLocalCargoProvider(cargoProvider) || !["trendyol", "hepsiburada", "n11", "pazarama", "idefix"].includes(channel)) {
    return [{ barcode: tracking, created_at: order?.cargo_created_at || "" }];
  }
  return [];
}

function getOrderCargoCodesForDisplay(order, fallbackProvider = "") {
  const localBarcodes = getOrderLocalBarcodes(order, fallbackProvider).map((item) => String(item?.barcode || "").trim()).filter(Boolean);
  if (localBarcodes.length > 0) return localBarcodes;

  const channel = getOrderChannel(order, fallbackProvider);
  if (channel === "n11") {
    const tracking = String(order?.n11_cargo_tracking || "").trim();
    return tracking ? [tracking] : [];
  }
  if (channel === "hepsiburada") {
    const packageNo = normalizeExternalOrderNo(order?.hb_package_number || order?.cargo_tracking || "");
    return packageNo ? [packageNo] : [];
  }
  if (channel === "trendyol") {
    const packageNo = getTrendyolPackageNo(order);
    return packageNo ? [packageNo] : [];
  }
  if (channel === "pazarama") {
    const tracking = normalizeExternalOrderNo(order?.pazarama_cargo_tracking || order?.cargo_tracking || "");
    if (tracking) return [tracking];
    const orderNo = normalizeExternalOrderNo(order?.pazarama_order_number || order?.channel_order_id || "");
    return orderNo ? [orderNo] : [];
  }
  if (channel === "idefix") {
    const tracking = normalizeExternalOrderNo(order?.idefix_cargo_tracking || order?.cargo_tracking || "");
    return tracking ? [tracking] : [];
  }
  return [];
}

function hasPrintableCargoLabel(order, fallbackProvider = "") {
  const channel = getOrderChannel(order, fallbackProvider);
  if (channel === "trendyol" || channel === "hepsiburada" || channel === "pazarama") return true;
  if (channel === "idefix") return getOrderCargoCodesForDisplay(order, fallbackProvider).length > 0;
  return getOrderCargoCodesForDisplay(order, fallbackProvider).length > 0;
}

function canTriggerCargoCreation(order, fallbackProvider = "") {
  const status = String(order?.status || "").trim().toLowerCase();
  if (["cancelled", "deleted"].includes(status)) return false;

  const channel = getOrderChannel(order, fallbackProvider);
  if (channel === "trendyol" || channel === "hepsiburada" || channel === "idefix" || channel === "pazarama") return false;
  if (channel === "n11") return !String(order?.n11_cargo_tracking || "").trim();
  return getOrderLocalBarcodes(order, fallbackProvider).length === 0;
}

function getOrderCargoDisplayValue(order, fallbackProvider = "") {
  const codes = getOrderCargoCodesForDisplay(order, fallbackProvider);
  if (codes.length > 1) return `${codes.length} Barkod`;
  if (codes.length === 1) return codes[0];
  return "Henuz yok";
}

const providerLogoMap = {
  hepsijet: "/integrations/hepsijet.svg",
  n11: "/integrations/n11.png",
  trendyol: "/integrations/trendyol.svg",
  hepsiburada: "/integrations/hepsiburada.svg",
  pazarama: "/integrations/pazarama.svg",
  idefix: "/integrations/idefix.svg",
  amazon: "/integrations/amazon.svg",
  shopify: "/integrations/shopify.svg",
  ikas: "/integrations/ikas.svg",
  woocommerce: "/integrations/woocommerce.svg",
};

function normalizeProviderKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("pazarama")) return "pazarama";
  if (raw.includes("hepsiburada")) return "hepsiburada";
  if (raw.includes("trendyol")) return "trendyol";
  if (raw === "woo") return "woocommerce";
  return raw;
}
const ORDERS_CACHE_TTL_MS = 60 * 1000;
const FORCE_FETCH_MIN_INTERVAL_MS = 8 * 1000;

// ─── Status Config ──────────────────────────────────
const ORDER_STATUSES = {
  delivered: { label: "Tamamlandı", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", type: "success" },
  completed: { label: "Tamamlandı", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", type: "success" },
  processing: { label: "Gönderime Hazır", cls: "text-orange-700 bg-orange-50 border-orange-200", type: "warning" },
  pending: { label: "Bekleyen", cls: "text-amber-700 bg-amber-50 border-amber-200", type: "warning" },
  shipped: { label: "Kargoda", cls: "text-indigo-700 bg-indigo-50 border-indigo-200", type: "info" },
  cancelled: { label: "İptal", cls: "text-rose-700 bg-rose-50 border-rose-200", type: "danger" },
  deleted: { label: "Silinmiş", cls: "text-zinc-700 bg-zinc-100 border-zinc-200", type: "neutral" },
};

const STATUS_OPTIONS = [
  { value: "pending", label: "Bekleyen" },
  { value: "processing", label: "Gönderime Hazır" },
  { value: "shipped", label: "Kargoda" },
  { value: "delivered", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal" },
  { value: "deleted", label: "Silinmiş" },
];

const TRENDYOL_STATUS_LABELS_TR = {
  Created: "Onay Bekliyor",
  UnPacked: "Hazırlık Öncesi",
  Picking: "Hazırlanıyor",
  Invoiced: "Faturalandı",
  ReadyForShipment: "Kargoya Hazır",
  Shipped: "Kargoda",
  AtCollectionPoint: "Toplama Noktasında",
  AtDistributionCenter: "Dağıtım Merkezinde",
  Delivered: "Teslim Edildi",
  UnDelivered: "Teslim Edilemedi",
  Cancelled: "İptal Edildi",
  UnSupplied: "Tedarik Edilemedi",
  Returned: "İade Edildi",
  InTransit: "Kargoda",
  OutForDelivery: "Dağıtımda",
  PackageInTransit: "Kargoda",
};

function getOrderStatusConfig(status) {
  return ORDER_STATUSES[status] || { label: status, cls: "text-foreground/80 bg-muted/50", type: "neutral" };
}

function getCargoTooltip(order, providerOrTrendyol = "") {
  const fallbackProvider = typeof providerOrTrendyol === "string"
    ? providerOrTrendyol
    : (providerOrTrendyol ? "trendyol" : "");
  const channel = getOrderChannel(order, fallbackProvider);
  const cargoError = String(order?.cargo_error_message || "").trim();
  if (cargoError) return `Kargo Hatası: ${cargoError}`;
  const codes = getOrderCargoCodesForDisplay(order, fallbackProvider);
  if (channel === "n11") {
    if (codes.length > 0) return `N11 Kod: ${codes[0]}`;
    const n11Status = String(order?.n11_status || "").trim().toLowerCase();
    if (n11Status === "created") return "N11 siparişi onaylanıp etiketi hazırlanacak";
    return "N11 etiketi henüz hazır değil";
  }
  if (codes.length > 0) {
    return codes.length > 1 ? `${codes.length} Barkod Hazır` : `Kargo: ${codes[0]}`;
  }
  if (channel === "trendyol") {
    const pkg = String(order?.trendyol_package_id || "").trim();
    if (pkg) return `Paket: ${pkg}`;
    return "Trendyol etiketi al";
  }
  if (channel === "hepsiburada") return "Hepsiburada etiketi hazır";
  if (channel === "idefix") {
    const idefixStatus = String(order?.idefix_status || "").trim().toLowerCase();
    if (idefixStatus === "shipment_ready" || idefixStatus === "created") {
      return "Önce siparişi kabul edip işleme almak gerekiyor";
    }
    if (idefixStatus === "shipment_picking") {
      return "Kargo kodu henüz oluşmadı, siparişi yeniden senkronize edin";
    }
    return "Idefix etiketi henüz hazır değil";
  }
  return "Kargo oluşturulmadı";
}

function hasRealInvoice(order) {
  return Boolean(
    order?.invoice_number ||
    order?.parasut_invoice_id ||
    order?.bizimhesap_invoice_guid
  );
}

function getTrendyolStatusUi(statusRaw) {
  const raw = String(statusRaw || "").trim();
  const label = TRENDYOL_STATUS_LABELS_TR[raw] || raw || "-";
  const code = raw.toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
  if (["shipped", "atcollectionpoint", "atdistributioncenter", "readyforshipment", "intransit", "outfordelivery", "packageintransit"].includes(code)) {
    return { label, cls: "border-violet-200 bg-violet-50 text-violet-700" };
  }
  if (["delivered"].includes(code)) {
    return { label, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (["cancelled", "unsupplied", "returned", "undelivered"].includes(code)) {
    return { label, cls: "border-rose-200 bg-rose-50 text-rose-700" };
  }
  return { label, cls: "border-amber-200 bg-amber-50 text-amber-700" };
}

function notificationKey(item) {
  return `${item.integration_id}:${item.channel_order_id}:${item.status}`;
}

function getOrderNotificationStorageKeys() {
  const userScope = String(localStorage.getItem("obihub_active_user_id") || "anon").trim() || "anon";
  return {
    orderNotifications: `obihub_order_notifications:${userScope}`,
    orderCount: `obihub_new_order_count:${userScope}`,
    dismissedOrders: `obihub_dismissed_order_notifications:${userScope}`,
  };
}

function appendOrderNotifications(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return { added_count: 0, added_processing_count: 0 };
  }
  const storageKeys = getOrderNotificationStorageKeys();
  const dismissed = (() => {
    try {
      const arr = JSON.parse(localStorage.getItem(storageKeys.dismissedOrders) || "[]");
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  })();
  const prev = (() => {
    try {
      return JSON.parse(localStorage.getItem(storageKeys.orderNotifications) || "[]");
    } catch {
      return [];
    }
  })();

  const prevUnique = [];
  const prevSeen = new Set();
  for (const x of prev) {
    const k = x?.id_key || notificationKey(x || {});
    if (!k || prevSeen.has(k)) continue;
    prevSeen.add(k);
    prevUnique.push({ ...x, id_key: k });
  }

  const seen = new Set(prevUnique.map((x) => x.id_key));
  const nextItems = [];
  for (const item of items) {
    const idKey = notificationKey(item);
    if (seen.has(idKey) || dismissed.has(idKey)) continue;
    seen.add(idKey);
    nextItems.push({
      ...item,
      id_key: idKey,
      created_at: new Date().toISOString(),
    });
  }

  const merged = [...nextItems, ...prevUnique].slice(0, 100);
  localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(merged));
  localStorage.setItem(storageKeys.orderCount, String(merged.length));
  window.dispatchEvent(
    new CustomEvent("obihub:new-orders", {
      detail: { count: merged.length, notifications: merged },
    })
  );
  return {
    added_count: nextItems.length,
    added_processing_count: nextItems.filter((x) => x.status === "processing").length,
  };
}

// ─── Icon Status Badge ──────────────────────────────
function IconBadge({ type, icon: Icon, tooltip, testId }) {
  const colors = {
    success: "bg-emerald-50 border-emerald-200 text-emerald-600",
    danger: "bg-rose-50 border-rose-200 text-rose-500",
    warning: "bg-amber-50 border-amber-200 text-amber-500",
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md border",
              colors[type] || colors.success
            )}
            data-testid={testId}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Order Detail Modal ─────────────────────────────
function OrderDetailModal({ order, open, onClose, onStatusChange, statusUpdating, provider }) {
  if (!order) return null;
  const statusConfig = getOrderStatusConfig(order.status);
  const modalStatusOptions = STATUS_OPTIONS;
  const providerKey = String(provider || "").toLowerCase();
  const isTrendyol = getOrderChannel(order, providerKey) === "trendyol";
  const trendyolStatus = getTrendyolStatusUi(order.trendyol_status);
  const cargoCodes = getOrderCargoCodesForDisplay(order, providerKey);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="order-detail-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground">
                Sipariş {getDisplayOrderNo(order)}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {formatFullDate(order.order_date || order.created_at)}
              </DialogDescription>
            </div>
          </div>

          {isTrendyol && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-border bg-muted/50/50 p-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Sipariş No</p>
                <p className="text-sm font-semibold text-slate-800">{getTrendyolOrderNo(order) || "-"}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50/50 p-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Paket No</p>
                <p className="text-sm font-semibold text-slate-800">{getTrendyolPackageNo(order) || "-"}</p>
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="px-6 py-5 space-y-6">
          {/* Status & Info Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-border bg-muted/50/50 p-3">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Durum</p>
              {isTrendyol ? (
                <div className={cn("inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold", trendyolStatus.cls)}>
                  {trendyolStatus.label}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {modalStatusOptions.map((st) => {
                    const active = order.status === st.value;
                    const meta = getOrderStatusConfig(st.value);
                    return (
                      <button
                        key={st.value}
                        type="button"
                        disabled={statusUpdating}
                        onClick={() => onStatusChange?.(order, st.value)}
                        className={cn(
                          "inline-flex h-7 items-center justify-center rounded-full border px-2.5 text-[11px] font-semibold transition-colors disabled:opacity-60",
                          active ? meta.cls : "border-border bg-card text-muted-foreground hover:bg-muted/50"
                        )}
                      >
                        {st.label}
                      </button>
                    );
                  })}
                  {order.status === "deleted" && (
                    <span className={cn("inline-flex h-7 items-center justify-center rounded-full border px-2.5 text-[11px] font-semibold", statusConfig.cls)}>
                      {statusConfig.label}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border bg-muted/50/50 p-3">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Kargo</p>
              <p className="text-sm font-medium text-slate-800">{getOrderCargoDisplayValue(order, providerKey)}</p>
              {order.cargo_error_message ? (
                <p className="mt-1 text-xs text-rose-700">{order.cargo_error_message}</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-border bg-muted/50/50 p-3">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Fatura</p>
              <p className="text-sm font-medium text-slate-800">{order.invoice_number || "Oluşturulmadı"}</p>
            </div>
          </div>

          {/* Items */}
          {order.items?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Sipariş Kalemleri</p>
              <div className="rounded-xl border border-border divide-y divide-slate-100 overflow-hidden">
                <div className="grid grid-cols-12 bg-muted/50 px-4 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <div className="col-span-6">Ürün</div>
                  <div className="col-span-2 text-center">Adet</div>
                  <div className="col-span-2 text-right">Birim Fiyat</div>
                  <div className="col-span-2 text-right">Toplam</div>
                </div>
                {order.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 px-4 py-3 items-center text-sm" data-testid={`order-item-${i}`}>
                    <div className="col-span-6">
                      <p className="font-medium text-slate-800 truncate">{item.product_name}</p>
                      {item.sku && <p className="text-[11px] text-muted-foreground">SKU: {item.sku}</p>}
                    </div>
                    <div className="col-span-2 text-center text-muted-foreground">{item.quantity}</div>
                    <div className="col-span-2 text-right text-muted-foreground">{formatCurrency(item.unit_price)}</div>
                    <div className="col-span-2 text-right font-semibold text-slate-800">{formatCurrency(item.total_price)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="flex items-center justify-between rounded-xl bg-slate-900 px-5 py-4">
            <span className="text-sm font-medium text-slate-300">Sipariş Toplamı</span>
            <span className="text-xl font-bold text-white">{formatCurrency(order.total_amount)}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Customer Detail Modal ──────────────────────────
function CustomerDetailModal({ order, open, onClose }) {
  if (!order) return null;
  const locationParts = [
    String(order.shipping_district || "").trim(),
    String(order.shipping_city || "").trim(),
  ].filter(Boolean);
  const locationLine = locationParts.length ? locationParts.join(" / ") : "";
  const deliveryAddress = String(order.shipping_address || "").trim();
  const deliveryAddressDisplay = deliveryAddress || locationLine ? [deliveryAddress, locationLine].filter(Boolean).join(" | ") : "Adres bilgisi yok";
  const cargoCodes = getOrderCargoCodesForDisplay(order);
  const hasPrintableLabel = hasPrintableCargoLabel(order);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="customer-detail-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center">
              <User className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground">
                {order.customer_name || "-"}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Fatura & Teslimat Bilgileri
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {/* Contact Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">İletişim Bilgileri</p>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3 text-sm">
                <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Ad Soyad</p>
                  <p className="font-medium text-slate-800">{order.customer_name || "-"}</p>
                </div>
              </div>
              {order.customer_email && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">E-posta</p>
                    <p className="font-medium text-slate-800">{order.customer_email}</p>
                  </div>
                </div>
              )}
              {order.customer_phone && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">Telefon</p>
                    <p className="font-medium text-slate-800">{order.customer_phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Fatura Bilgileri</p>
            <div className="rounded-xl border border-border bg-muted/50/50 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Fatura No</span>
                <span className="font-medium text-slate-800">{order.invoice_number || "Oluşturulmadı"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Fatura Durumu</span>
                <span className={cn("font-medium", order.invoice_number ? "text-emerald-600" : "text-amber-600")}>
                  {order.invoice_number ? "Faturalandı" : "Bekliyor"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Sipariş Tutarı</span>
                <span className="font-semibold text-slate-800">{formatCurrency(order.total_amount)}</span>
              </div>
            </div>
          </div>

          {/* Delivery Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Teslimat Bilgileri</p>
            <div className="rounded-xl border border-border bg-muted/50/50 p-4 space-y-2">
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground">Teslimat Adresi</p>
                  <p className="font-medium text-slate-800">{deliveryAddressDisplay}</p>
                </div>
              </div>
              {hasPrintableLabel && (
                <div className="flex items-start gap-3 text-sm pt-2 border-t border-border">
                  <Truck className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-[11px] text-muted-foreground">Kargo Takip No</p>
                    {cargoCodes.map((code, i) => (
                      <p key={i} className="font-medium text-slate-800 font-mono text-xs">{code}</p>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 text-sm pt-2 border-t border-border">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground">Sipariş Tarihi</p>
                  <p className="font-medium text-slate-800">{formatFullDate(order.order_date || order.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Kargo Fişi Sidebar ─────────────────────────────
import CargoLabelSidebar from "@/components/CargoLabelSidebar";

// Main page
export default function IntegrationOrdersPage() {
  const { id: integrationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastNotificationRefreshRef = useRef(null);
  const lastForcedFetchAtRef = useRef(0);
  const ordersFetchSeqRef = useRef(0);
  const integrationFetchSeqRef = useRef(0);

  const [integration, setIntegration] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [invoicing, setInvoicing] = useState(null);
  const [shippingOrder, setShippingOrder] = useState(null);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("-order_date");
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState({});
  const [orderDetailOpen, setOrderDetailOpen] = useState(null);
  const [customerDetailOpen, setCustomerDetailOpen] = useState(null);
  const [cargoSidebarOpen, setCargoSidebarOpen] = useState(false);
  const [cargoSidebarOrders, setCargoSidebarOrders] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [takingProcessingId, setTakingProcessingId] = useState(null);
  const [sharingInvoiceId, setSharingInvoiceId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncDateMode, setSyncDateMode] = useState("last7"); // last7 | custom | full
  const [syncDateFrom, setSyncDateFrom] = useState("");
  const [syncDateTo, setSyncDateTo] = useState("");
  const [syncOrderNumber, setSyncOrderNumber] = useState("");
  const ORDER_LIST_CACHE_PREFIX = "obihub_orders_cache_v2";
  const provider = normalizeProviderKey(integration?.provider || "");
  const isTrendyolProvider = provider === "trendyol";
  const isHepsiburadaProvider = provider === "hepsiburada";
  const isN11Provider = provider === "n11";
  const isIdefixProvider = provider === "idefix";
  const providerLogo = providerLogoMap[provider];

  useEffect(() => {
    // Reset transient filters/state when switching integration to avoid carrying
    // a stale query/status/page that may produce empty lists on the next channel.
    setQ("");
    setStatusFilter("all");
    setSort("-order_date");
    setPage(1);
    setSelected({});
    setOpenMenuId(null);
    setOrderDetailOpen(null);
    setCustomerDetailOpen(null);
    setCargoSidebarOpen(false);
    setCargoSidebarOrders([]);
    setOrders([]);
    setStats({});
    setTotal(0);
    setTotalPages(1);
  }, [integrationId]);

  const playNewOrderSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

      const o1 = ctx.createOscillator();
      o1.type = "triangle";
      o1.frequency.setValueAtTime(880, now);
      o1.connect(gain);
      o1.start(now);
      o1.stop(now + 0.11);

      const o2 = ctx.createOscillator();
      o2.type = "triangle";
      o2.frequency.setValueAtTime(1320, now + 0.12);
      o2.connect(gain);
      o2.start(now + 0.12);
      o2.stop(now + 0.28);
    } catch {
      // ignore sound errors
    }
  };

  // Fetch integration info
  useEffect(() => {
    (async () => {
      const reqSeq = ++integrationFetchSeqRef.current;
      let detail = null;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          detail = await api.get(`/integrations/${integrationId}`, {
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          });
          break;
        } catch {
          if (attempt < 3) await sleep(250 * attempt);
        }
      }
      if (reqSeq !== integrationFetchSeqRef.current) return;
      if (detail && typeof detail === "object") {
        setIntegration(detail);
        return;
      }
      // Fallback: resolve from full list if detail endpoint is unavailable.
      let list = null;
      try { list = await api.get("/integrations"); } catch {}
      const rows = normalizeListRows(list);
      if (reqSeq !== integrationFetchSeqRef.current) return;
      setIntegration(rows.find((i) => i.id === integrationId) || null);
    })();
  }, [integrationId]);

  // Fetch orders
  const fetchOrders = useCallback(async ({ force = false } = {}) => {
    const reqSeq = ++ordersFetchSeqRef.current;
    const cacheKey = `${ORDER_LIST_CACHE_PREFIX}:${integrationId}:${page}:${perPage}:${sort}:${statusFilter}:${q}`;
    if (!force) {
      try {
        const cachedRaw = sessionStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          const ts = Number(cached?.ts || 0);
          if (ts > 0 && Date.now() - ts < ORDERS_CACHE_TTL_MS) {
            const rows = normalizeListRows(cached?.data || {});
            if (rows.length === 0) {
              // Never trust empty cache entries; they are typically transient API responses.
              throw new Error("EMPTY_CACHE_MISS");
            }
            const totalCount = normalizeTotal(cached?.data || {}, rows.length);
            const pageCount = normalizeTotalPages(cached?.data || {}, totalCount, perPage);
            setOrders(rows);
            setTotal(totalCount);
            setTotalPages(pageCount);
            setStats(cached?.stats || {});
            setLoading(false);
            return;
          }
        }
      } catch {
        // ignore cache parse errors
      }
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        integration_id: integrationId,
        page: String(page),
        per_page: String(perPage),
        sort_by: sort,
      });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (q) params.set("search", q);

      // Orders are critical for page render; stats are best-effort and must never
      // block list rendering.
      let res = null;
      let statsRes = {};
      let lastErr = null;
      let retriesOnEmpty = 0;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          res = await api.get(`/orders?${params}`, {
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          });
          const rowsProbe = normalizeListRows(res);
          const shouldRetryOnEmpty =
            rowsProbe.length === 0 &&
            statusFilter === "all" &&
            !q &&
            retriesOnEmpty < 1;
          if (shouldRetryOnEmpty) {
            retriesOnEmpty += 1;
            await sleep(300);
            continue;
          }
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (attempt < 3) await sleep(250 * attempt);
        }
      }
      if (lastErr) throw lastErr;
      lastForcedFetchAtRef.current = Date.now();

      try {
        statsRes = await api.get(`/orders/stats?integration_id=${integrationId}`, {
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          retry_attempts: 2,
        });
      } catch (statsErr) {
        console.warn("Orders stats fetch failed, continuing with list render:", statsErr);
      }

      if (reqSeq !== ordersFetchSeqRef.current) return;
      let rows = normalizeListRows(res);
      let totalCount = normalizeTotal(res, rows.length);
      let pageCount = normalizeTotalPages(res, totalCount, perPage);

      // Consistency guard:
      // 1) If list is empty while stats says there are records, force one extra fetch.
      // 2) If current page is out of range after channel switch, fall back to page 1.
      if (statusFilter === "all" && !q && rows.length === 0) {
        if (Number(statsRes?.total || 0) > 0) {
          try {
            const retryRes = await api.get(`/orders?${params}`, {
              headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
              retry_attempts: 2,
            });
            rows = normalizeListRows(retryRes);
            totalCount = normalizeTotal(retryRes, rows.length);
            pageCount = normalizeTotalPages(retryRes, totalCount, perPage);
            res = retryRes;
          } catch {
            // keep original empty result
          }
        }
        if (rows.length === 0 && page > 1) {
          try {
            params.set("page", "1");
            const pageOneRes = await api.get(`/orders?${params}`, {
              headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
              retry_attempts: 2,
            });
            const pageOneRows = normalizeListRows(pageOneRes);
            if (pageOneRows.length > 0) {
              rows = pageOneRows;
              totalCount = normalizeTotal(pageOneRes, pageOneRows.length);
              pageCount = normalizeTotalPages(pageOneRes, totalCount, perPage);
              res = pageOneRes;
              setPage(1);
            }
          } catch {
            // keep current state
          }
        }
      }
      // Keep list deterministic: always bind UI to current response/current cache key.
      // If the API is empty, we still render empty rather than leaking previous integration rows.
      setOrders(rows);
      setTotal(totalCount);
      setTotalPages(pageCount);
      setStats(statsRes || {});
      // Cache only non-empty lists to avoid propagating transient empties across navigations.
      if (rows.length > 0) {
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              ts: Date.now(),
              data: res || {},
              stats: statsRes || {},
            })
          );
        } catch {
          // ignore cache write errors
        }
      }
    } catch (err) {
      if (reqSeq !== ordersFetchSeqRef.current) return;
      console.error("Orders fetch failed:", err);
      toast.error("Siparişler yüklenemedi. Lütfen tekrar deneyin.");
    } finally {
      if (reqSeq !== ordersFetchSeqRef.current) return;
      setLoading(false);
    }
  }, [integrationId, page, perPage, sort, statusFilter, q]);

  useEffect(() => { fetchOrders({ force: false }); }, [fetchOrders]);
  useEffect(() => { setPage(1); }, [statusFilter, q, sort]);
  useEffect(() => {
    const state = location.state || {};
    if (!state.fromNotification) return;
    const refreshAt = state.refreshAt || "notif";
    if (lastNotificationRefreshRef.current === refreshAt) return;
    lastNotificationRefreshRef.current = refreshAt;
    fetchOrders({ force: true });
  }, [location.state, fetchOrders]);
  useEffect(() => {
    const onNewOrders = (event) => {
      const incoming = Array.isArray(event?.detail?.notifications) ? event.detail.notifications : [];
      const hasCurrentIntegration = incoming.some((n) => String(n?.integration_id || "") === String(integrationId));
      if (hasCurrentIntegration) {
        // New-order notification arrived for this integration.
        // Always bypass cache so the list reflects the order immediately.
        fetchOrders({ force: true });
      }
    };
    window.addEventListener("obihub:new-orders", onNewOrders);
    return () => window.removeEventListener("obihub:new-orders", onNewOrders);
  }, [integrationId, fetchOrders]);
  useEffect(() => {
    if (!orderDetailOpen?.id) return;
    const latest = orders.find((o) => o.id === orderDetailOpen.id);
    if (latest) setOrderDetailOpen(latest);
  }, [orders, orderDetailOpen?.id]);

  // Sync
  const runSync = useCallback(async ({ onlyProcessing = false, silent = false, filters = {} } = {}) => {
    if (!silent) setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (onlyProcessing) {
        params.set("status", integration?.provider === "trendyol" ? "Created" : "processing");
      }
      if (filters?.days) params.set("days", String(filters.days));
      if (filters?.full) params.set("full", "true");
      if (filters?.all_statuses) params.set("all_statuses", "true");
      if (filters?.date_from) params.set("date_from", filters.date_from);
      if (filters?.date_to) params.set("date_to", filters.date_to);
      if (filters?.order_number) params.set("order_number", filters.order_number);
      const provider = String(integration?.provider || "woocommerce").toLowerCase();
      const basePath = provider === "trendyol"
        ? `/trendyol/${integrationId}/sync-orders`
        : provider === "hepsiburada"
          ? (filters?.by_order_lookup
            ? `/hepsiburada/${integrationId}/sync-order-by-number`
            : `/hepsiburada/${integrationId}/sync-orders`)
          : provider === "n11"
            ? `/n11/${integrationId}/sync-orders`
            : provider === "pazarama"
              ? `/pazarama/${integrationId}/sync-orders`
              : provider === "idefix"
                ? `/idefix/${integrationId}/sync-orders`
                : provider === "amazon"
                  ? `/amazon/${integrationId}/sync-orders`
                  : `/woocommerce/${integrationId}/sync-orders`;
      const syncUrl = `${basePath}${params.toString() ? `?${params.toString()}` : ""}`;
      const amazonBody = provider === "amazon" ? { days_back: filters?.days || 30 } : undefined;
      const result = await api.post(syncUrl, amazonBody);
      if (result.success) {
        if (!silent) {
          const isTrendyol = provider === "trendyol";
          if (isTrendyol) {
            const fetched = Number(result?.total_fetched || 0);
            const created = Number(result?.count || 0);
            toast.success(`${result.message || "Senkronizasyon tamamlandı"} (Toplam: ${fetched}, Yeni: ${created})`);
          } else if (provider === "hepsiburada") {
            const fetched = Number(result?.total_fetched || 0);
            const created = Number(result?.count || 0);
            const updated = Number(result?.updated_count || 0);
            toast.success(`${result.message || "Senkronizasyon tamamlandı"} (Toplam: ${fetched}, Yeni: ${created}, Güncellenen: ${updated})`);
          } else if (provider === "amazon") {
            const created = Number(result?.created || 0);
            const updated = Number(result?.updated || 0);
            toast.success(`Amazon siparişler güncellendi: ${created} yeni, ${updated} güncellendi.`);
          } else {
            toast.success(result.message || "Senkronizasyon tamamlandı");
          }
        }
        const newOrders = Array.isArray(result.new_orders) ? result.new_orders : [];
        const normalized = newOrders
          .filter((o) => o && (o.status === "pending" || o.status === "processing"))
          .map((o) => ({
            integration_id: o.integration_id || integrationId,
            channel_order_id: o.channel_order_id,
            customer_name: o.customer_name || "Müşteri",
            total_amount: Number(o.total_amount || 0),
            currency: o.currency || "TRY",
            status: o.status,
            status_label:
              o.status === "processing"
                ? "Gönderime Hazır"
                : "Bekleyen",
            route: withPanelBase(`/panel/entegrasyonlar/${integrationId}/siparisler`, null, location.pathname),
          }));

        if (normalized.length > 0) {
          const notifResult = appendOrderNotifications(normalized);
          if ((notifResult?.added_processing_count || 0) > 0) {
            playNewOrderSound();
          }
        }
        if (!onlyProcessing && String(integration?.provider || "").toLowerCase() === "woocommerce") {
          try { await api.post(`/orders/resync-dates?integration_id=${integrationId}`); } catch {}
        }
        await fetchOrders();
      }
    } catch (e) {
      if (!silent) toast.error(e.message || "Senkronizasyon başarısız");
    } finally {
      if (!silent) setSyncing(false);
    }
  }, [integrationId, fetchOrders, integration?.provider, location.pathname]);

  const handleSync = async () => {
    if (!isTrendyolProvider && syncDateMode === "full") {
      setSyncDateMode("last7");
    }
    setSyncModalOpen(true);
  };

  const handleSyncWithFilters = async () => {
    const orderNo = (syncOrderNumber || "").trim();
    const isTrendyol = String(integration?.provider || "").toLowerCase() === "trendyol";
    const maxDiffDays = isTrendyol ? 14 : 15;
    const hasOrderNumber = orderNo.length > 0;

    if (hasOrderNumber) {
      setSyncModalOpen(false);
      await runSync({
        onlyProcessing: false,
        silent: false,
        filters: {
          by_order_lookup: String(integration?.provider || "").toLowerCase() === "hepsiburada",
          order_number: orderNo,
        },
      });
      return;
    }

    if (isTrendyol && syncDateMode === "full") {
      setSyncModalOpen(false);
      await runSync({
        onlyProcessing: false,
        silent: false,
        filters: {
          full: true,
          all_statuses: true,
          order_number: orderNo || undefined,
        },
      });
      return;
    }

    if (syncDateMode === "custom") {
      if (!syncDateFrom || !syncDateTo) {
        toast.error("Tarih aralığı için başlangıç ve bitiş tarihi girin");
        return;
      }
      const from = new Date(`${syncDateFrom}T00:00:00`);
      const to = new Date(`${syncDateTo}T23:59:59`);
      if (isNaN(from.getTime()) || isNaN(to.getTime())) {
        toast.error("Tarih formatı geçersiz");
        return;
      }
      if (to < from) {
        toast.error("Bitiş tarihi başlangıçtan küçük olamaz");
        return;
      }
      const diffDays = (to - from) / (1000 * 60 * 60 * 24);
      if (diffDays > maxDiffDays) {
        toast.error(`Sipariş aralığı en fazla ${maxDiffDays} gün olabilir`);
        return;
      }
      setSyncModalOpen(false);
      await runSync({
        onlyProcessing: false,
        silent: false,
        filters: {
          date_from: syncDateFrom,
          date_to: syncDateTo,
          all_statuses: isTrendyol ? true : undefined,
          order_number: orderNo || undefined,
        },
      });
      return;
    }

    setSyncModalOpen(false);
    await runSync({
      onlyProcessing: false,
      silent: false,
      filters: {
        days: isTrendyol ? 30 : 15,
        all_statuses: isTrendyol ? true : undefined,
        order_number: orderNo || undefined,
      },
    });
  };

  const handleStatusChange = async (order, newStatus) => {
    if (!order?.id || !newStatus || order.status === newStatus) return;
    const prev = order.status;
    setStatusUpdatingId(order.id);
    setOrders((curr) => curr.map((x) => (x.id === order.id ? { ...x, status: newStatus } : x)));
    if (orderDetailOpen?.id === order.id) {
      setOrderDetailOpen((prevOpen) => (prevOpen ? { ...prevOpen, status: newStatus } : prevOpen));
    }
    try {
      await api.patch(`/orders/${order.id}`, { status: newStatus });
      toast.success("Sipariş durumu güncellendi");
      await fetchOrders();
    } catch (e) {
      setOrders((curr) => curr.map((x) => (x.id === order.id ? { ...x, status: prev } : x)));
      if (orderDetailOpen?.id === order.id) {
        setOrderDetailOpen((prevOpen) => (prevOpen ? { ...prevOpen, status: prev } : prevOpen));
      }
      toast.error(e.message || "Durum güncellenemedi");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleTakeInProcessing = async (order) => {
    if (!order?.id) return;
    setTakingProcessingId(order.id);
    try {
      const channel = getOrderChannel(order, provider);
      const endpoint = channel === "idefix"
        ? `/idefix/orders/${order.id}/take-in-processing`
        : `/trendyol/orders/${order.id}/take-in-processing`;
      const result = await api.post(endpoint);
      if (result?.success) {
        toast.success(result.message || "Sipariş işleme alındı");
        await fetchOrders();
      } else {
        toast.error(result?.message || "Sipariş işleme alınamadı");
      }
    } catch (e) {
      toast.error(e.message || "Sipariş işleme alınamadı");
    } finally {
      setTakingProcessingId(null);
    }
  };

  const handleShareInvoiceToStore = async (order) => {
    if (!order?.id) return;
    if (!order?.parasut_invoice_id) {
      toast.error("Önce fatura oluşturulmalı");
      return;
    }
    setSharingInvoiceId(order.id);
    setOpenMenuId(null);
    try {
      const result = await api.post(`/orders/${order.id}/share-invoice-to-store`);
      if (result?.success) {
        toast.success(result.message || "Fatura mağazaya iletildi");
      } else {
        toast.error(result?.message || "Fatura mağazaya iletilemedi");
      }
      await fetchOrders();
    } catch (e) {
      toast.error(e.message || "Fatura mağazaya iletilemedi");
    } finally {
      setSharingInvoiceId(null);
    }
  };

  const handleOpenInvoice = (order) => {
    if (!order) return;
    if (order.parasut_invoice_id) {
      navigate(
        withPanelBase(
          `/panel/satislar/faturalar/${order.parasut_invoice_id}`,
          null,
          location.pathname
        )
      );
      return;
    }
    if (order.parasut_invoice_url) {
      window.open(order.parasut_invoice_url, "_blank", "noopener,noreferrer");
      return;
    }
    toast.error("Bu sipariş için henüz fatura oluşturulmamış");
  };

  // Invoice
  const handleInvoice = async (order) => {
    if (!order?.id) return;
    if (order.parasut_invoice_id || order.parasut_invoice_url) {
      handleOpenInvoice(order);
      return;
    }
    setInvoicing(order.id);
    try {
      const result = await api.post(`/orders/${order.id}/create-invoice`);
      if (result.success) { toast.success(result.message); await fetchOrders(); }
      else toast.error(result.message || "Fatura oluşturulamadı");
    } catch (e) { toast.error(e.message || "Fatura hatası"); }
    finally { setInvoicing(null); }
  };

  // Create HepsiJet Shipment
  const handleCreateShipment = async (orderId) => {
    setShippingOrder(orderId);
    try {
      if (isTrendyolProvider) {
        // Use the premium/table label design from CargoLabelSidebar for Trendyol.
        openPrintSidebar([orderId]);
        toast.success("Trendyol etiket paneli açıldı");
      } else if (isHepsiburadaProvider) {
        openPrintSidebar([orderId]);
        toast.success("Hepsiburada etiket paneli açıldı");
      } else {
        const result = await api.post(`/hepsijet/create-shipment/${orderId}`);
        if (result.success) {
          toast.success(result.message || "Kargo oluşturuldu");
          await fetchOrders();
          if ((isN11Provider || isIdefixProvider) && result.label_ready) {
            openPrintSidebar([orderId]);
          }
        } else {
          toast.error(result.message || "Kargo oluşturulamadı");
        }
      }
    } catch (e) {
      toast.error(
        e.message || (
          isTrendyolProvider
            ? "Trendyol etiket hatası"
            : (isHepsiburadaProvider ? "Hepsiburada etiket hatası" : (isIdefixProvider ? "Idefix etiket hatası" : "Kargo oluşturma hatası"))
        )
      );
    } finally {
      setShippingOrder(null);
    }
  };

  const openPrintSidebar = (orderIds) => {
    const ids = Array.isArray(orderIds) ? orderIds : [];
    if (ids.length === 0) {
      toast.error("Etiket yazdırılacak sipariş yok");
      return;
    }
    setCargoSidebarOrders(orderIds || []);
    setCargoSidebarOpen(true);
  };

  // Bulk create shipments
  const handleBulkShipment = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    const ordersToShip = orders.filter((o) => {
      if (!ids.includes(o.id)) return false;
      if (isTrendyolProvider || isHepsiburadaProvider) return true;
      return canTriggerCargoCreation(o, provider);
    });
    if (ordersToShip.length === 0) { toast.error("Kargo oluşturulacak sipariş bulunamadı"); return; }
    for (const o of ordersToShip) { await handleCreateShipment(o.id); }
  };

  // Selection helpers
  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected]
  );
  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
  const deletableSelectedCount = useMemo(
    () => orders.filter((o) => selected[o.id] && o.status === "deleted").length,
    [orders, selected]
  );
  const allChecked = useMemo(() => orders.length > 0 && orders.every((o) => selected[o.id]), [orders, selected]);

  const toggleAll = (checked) => {
    const next = {};
    orders.forEach((o) => (next[o.id] = checked));
    setSelected(next);
  };

  const handleBulkDeleteDeleted = async () => {
    const ids = orders.filter((o) => selectedIds.includes(o.id) && o.status === "deleted").map((o) => o.id);
    if (ids.length === 0) {
      toast.error("Silinebilir sipariş yok. Sadece 'Silinmiş' durumundakiler silinebilir.");
      return;
    }
    try {
      const res = await api.post("/orders/bulk-delete", { order_ids: ids });
      toast.success(`${res.deleted_count || ids.length} sipariş silindi`);
      setSelected({});
      await fetchOrders();
    } catch (e) {
      toast.error(e.message || "Toplu silme başarısız");
    }
  };

  // Badge logic per order (matches Yengeç: delivered=green, cancelled=red invoice)
  function getInvoiceBadgeType(order) {
    if (order.status === "cancelled") return "danger";
    if (order.status === "deleted") return "danger";
    if (order.status === "delivered" || order.status === "completed") return "success";
    if (hasRealInvoice(order)) return "success";
    return "warning";
  }
  function getBarcodeBadgeType(order) {
    if (hasPrintableCargoLabel(order, provider)) return "success";
    if (order.status === "cancelled" || order.status === "deleted") return "danger";
    if (order.cargo_error_message) return "danger";
    return "warning";
  }
  function getSlipBadgeType(order) {
    if (hasPrintableCargoLabel(order, provider)) return "success";
    if (order.cargo_error_message) return "danger";
    return "warning";
  }

  // Pagination
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxV = 5;
    if (totalPages <= maxV) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      for (let i = 1; i <= maxV; i++) pages.push(i);
    } else if (page >= totalPages - 2) {
      for (let i = totalPages - maxV + 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = page - 2; i <= page + 2; i++) pages.push(i);
    }
    return pages;
  }, [page, totalPages]);

  return (
    <div className="page-shell w-full min-h-[calc(100dvh-64px)] space-y-4" data-testid="integration-orders-page">
      <Dialog open={syncModalOpen} onOpenChange={setSyncModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sync-orders-filter-modal">
          <DialogHeader>
            <DialogTitle>Mağazadan Siparişleri Çek</DialogTitle>
            <DialogDescription>
              Varsayılan son {isTrendyolProvider ? 30 : 15} gün çekilir. Özel tarih aralığı en fazla {isTrendyolProvider ? 14 : 15} gün olabilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Sipariş Tarihi</label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="syncDateMode"
                  checked={syncDateMode === "last7"}
                  onChange={() => setSyncDateMode("last7")}
                  disabled={(syncOrderNumber || "").trim().length > 0}
                />
                Son {isTrendyolProvider ? 30 : 15} Gün
              </label>
              {isTrendyolProvider && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="syncDateMode"
                    checked={syncDateMode === "full"}
                    onChange={() => setSyncDateMode("full")}
                    disabled={(syncOrderNumber || "").trim().length > 0}
                  />
                  Tüm Geçmiş Siparişler
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="syncDateMode"
                  checked={syncDateMode === "custom"}
                  onChange={() => setSyncDateMode("custom")}
                  disabled={(syncOrderNumber || "").trim().length > 0}
                />
                Seçilen Tarih Aralığı
              </label>
              {syncDateMode === "custom" && (syncOrderNumber || "").trim().length === 0 && (
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={syncDateFrom}
                    onChange={(e) => setSyncDateFrom(e.target.value)}
                    className="h-9 rounded-md border border-border px-2 text-sm"
                  />
                  <input
                    type="date"
                    value={syncDateTo}
                    onChange={(e) => setSyncDateTo(e.target.value)}
                    className="h-9 rounded-md border border-border px-2 text-sm"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sipariş Numarası</label>
              <input
                type="text"
                value={syncOrderNumber}
                onChange={(e) => setSyncOrderNumber(e.target.value)}
                placeholder="Örn: 16162 veya WC-16162"
                className="h-9 w-full rounded-md border border-border px-3 text-sm"
              />
              {(syncOrderNumber || "").trim().length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Sipariş numarası girildiği için tarih filtresi uygulanmayacak.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSyncModalOpen(false)}
                className="h-9 rounded-md border border-border px-4 text-sm font-medium text-foreground/80 hover:bg-muted/50"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleSyncWithFilters}
                className="h-9 rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Siparişleri Çek
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Top Bar ─── */}
      <div className="surface-card overflow-hidden">
        <div className="sheet-heading flex-wrap gap-y-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/panel/entegrasyonlar")}
              className="h-8 w-8 rounded-lg border border-border bg-background hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0"
              data-testid="back-btn"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-lg border border-border bg-background inline-flex items-center justify-center overflow-hidden shrink-0">
                {providerLogo ? (
                  <img src={providerLogo} alt={provider || "entegrasyon"} className="h-5 w-5 object-contain" />
                ) : (
                  <span className="text-[10px] font-bold text-muted-foreground">
                    {(integration?.provider || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 data-testid="page-title">Sipariş Listesi</h2>
                {!loading && <p>{total} sipariş</p>}
              </div>
            </div>
          </div>

          <div className="sheet-heading-actions">
            {stats.needs_invoice > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700" data-testid="needs-invoice-badge">
                <AlertTriangle className="h-3.5 w-3.5" />
                Faturalandırılacak: {stats.needs_invoice}
              </span>
            )}
            <div className="relative hidden md:block w-[280px] lg:w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm outline-none focus:border-border focus:ring-1 focus:ring-border transition-all"
                placeholder="Sipariş No veya müşteri adı..."
                data-testid="search-orders-input-desktop"
              />
            </div>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-sm font-medium text-foreground/80 hover:bg-muted/50 disabled:opacity-50 transition-colors"
              data-testid="sync-orders-btn"
              title={syncing ? "Çekiliyor..." : "Siparişleri Çek"}
            >
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* ─── Filters ─── */}
        <div className="px-6 py-3 border-t border-border flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:order-1">
            {selectedCount > 0 && (
              <>
                <button
                  onClick={() => { const ids = Object.entries(selected).filter(([,v]) => v).map(([k]) => k); openPrintSidebar(ids); }}
                  className="inline-flex h-9 w-full sm:w-auto max-w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-3.5 text-sm font-medium text-foreground/80 hover:bg-muted/50 transition-colors whitespace-nowrap"
                  data-testid="bulk-print-btn"
                >
                  <Printer className="h-3.5 w-3.5" /> Kargo Fişi
                </button>
                <button
                  onClick={handleBulkDeleteDeleted}
                  disabled={deletableSelectedCount === 0}
                  className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50 disabled:hover:bg-rose-50 transition-colors sm:w-auto"
                  data-testid="bulk-delete-deleted-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Silinmişleri Sil
                </button>
                <span className="inline-flex items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700" data-testid="selected-count-badge">
                  {selectedCount} seçili{deletableSelectedCount > 0 ? ` • ${deletableSelectedCount} silinebilir` : ""}
                </span>
              </>
            )}
          </div>
          <div className="relative w-full md:hidden sm:order-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm outline-none focus:border-border focus:ring-1 focus:ring-border transition-all"
              placeholder="Sipariş No veya müşteri adı..."
              data-testid="search-orders-input"
            />
          </div>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="table-shell overflow-hidden">
        {/* Mobile cards */}
        <div className="space-y-2 p-3 md:hidden">
          {!loading && orders.length > 0 && (
            <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-foreground/80">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
                Tümünü seç
              </label>
              <span className="text-xs font-semibold text-muted-foreground">{selectedCount} seçili</span>
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-border bg-card px-4 py-12 text-center">
              <Loader2 className="h-7 w-7 mx-auto animate-spin text-slate-300" />
              <p className="mt-3 text-sm text-muted-foreground">Siparişler yükleniyor...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="rounded-xl border border-border bg-card px-4 py-12 text-center">
              <Package className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-muted-foreground">Sipariş bulunamadı.</p>
              <p className="text-xs text-muted-foreground mt-1">Filtreleri değiştirin veya siparişleri çekin.</p>
            </div>
          ) : (
            orders.map((o) => {
              const isSelected = !!selected[o.id];
              const statusCfg = getOrderStatusConfig(o.status);
              const orderChannel = getOrderChannel(o, provider);
              const isTrendyolOrder = orderChannel === "trendyol";
              const isHepsiburadaOrder = orderChannel === "hepsiburada";
              const isPazaramaOrder = orderChannel === "pazarama";
              const isIdefixOrder = orderChannel === "idefix";
              const needsIdefixAccept = isIdefixOrder && ["shipment_ready", "created"].includes(String(o.idefix_status || "").toLowerCase());
              const hasPrintableLabel = hasPrintableCargoLabel(o, provider);
              const canCreateCargo = canTriggerCargoCreation(o, provider);
              const cargoTooltip = getCargoTooltip(o, provider);
              const printTooltip = isTrendyolOrder
                ? "Trendyol etiketi yazdır"
                : (isHepsiburadaOrder
                  ? "Hepsiburada etiketini yazdır"
                  : (orderChannel === "n11"
                    ? (hasPrintableLabel ? "N11 etiketini yazdır" : "Önce etiketi hazırla")
                    : (hasPrintableLabel ? "Etiket hazır" : "Etiket bekliyor")));
              const invoiceSentToTrendyol = isTrendyolOrder && Boolean(o.trendyol_invoice_shared);
              const hasCreatedInvoice = hasRealInvoice(o);
              const canOpenInvoice = Boolean(o.parasut_invoice_id || o.parasut_invoice_url);
              const isInvoiced = Boolean(
                hasRealInvoice(o) ||
                (isHepsiburadaOrder && o.hb_invoice_missing !== true)
              );
              const needsInvoice = isHepsiburadaOrder
                ? (o.hb_invoice_missing === true && !["cancelled", "deleted"].includes(o.status))
                : (!isInvoiced && !["cancelled", "deleted"].includes(o.status));
              const invoiceBadgeType = getInvoiceBadgeType(o);

              return (
                <div
                  key={o.id}
                  className={cn(
                    "rounded-xl border border-border bg-card p-3",
                    isSelected && "border-blue-300 bg-blue-50/30"
                  )}
                  data-testid={`order-card-${o.id}`}
                >
                  {(isTrendyolProvider && String(o.trendyol_status || "").toLowerCase() === "created") || needsIdefixAccept ? (
                    <button
                      className="mb-2 inline-flex h-8 w-full items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-700"
                      onClick={() => handleTakeInProcessing(o)}
                      disabled={takingProcessingId === o.id}
                    >
                      {takingProcessingId === o.id ? "İşleniyor..." : "İşleme Al"}
                    </button>
                  ) : null}

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        className="block truncate text-sm font-semibold text-foreground"
                        onClick={() => setOrderDetailOpen(o)}
                      >
                        {getDisplayOrderNo(o)}
                      </button>
                      {getDisplayPackageNo(o) ? (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{getDisplayPackageNo(o)}</p>
                      ) : null}
                      <button
                        className="mt-0.5 block truncate text-sm text-muted-foreground"
                        onClick={() => setCustomerDetailOpen(o)}
                      >
                        {o.customer_name || "-"}
                      </button>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => setSelected((prev) => ({ ...prev, [o.id]: checked }))}
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatOrderDate(o.order_date || o.created_at)}</span>
                    <span className="font-semibold text-foreground">{formatCurrency(o.total_amount)}</span>
                  </div>

                  <div className="mt-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "inline-flex h-7 min-w-[108px] items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium border whitespace-nowrap",
                          statusCfg.cls
                        )}
                      >
                        {statusCfg.label}
                      </span>
                      <div className="mt-2 grid w-[108px] grid-cols-3 gap-1.5">
                        <IconBadge
                          type={invoiceBadgeType}
                          icon={FileText}
                          tooltip={invoiceBadgeType === "success" ? "Faturalandı" : "Fatura bekliyor"}
                        />
                        <IconBadge
                          type={getBarcodeBadgeType(o)}
                          icon={ScanBarcode}
                          tooltip={cargoTooltip}
                        />
                        <IconBadge
                          type={getSlipBadgeType(o)}
                          icon={Printer}
                          tooltip={printTooltip}
                        />
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      {isHepsiburadaOrder ? (
                        <button
                          className={cn(
                            "inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-card",
                            canOpenInvoice ? "border-border" : "border-border opacity-40 cursor-not-allowed"
                          )}
                          onClick={() => canOpenInvoice && handleOpenInvoice(o)}
                          disabled={!canOpenInvoice}
                          title={canOpenInvoice ? "Faturayı aç" : "Fatura oluşturulmadı"}
                        >
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      ) : needsInvoice ? (
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50"
                          onClick={() => handleInvoice(o)}
                          disabled={invoicing === o.id}
                          title="Faturalandır"
                        >
                          {invoicing === o.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                          ) : (
                            <FileText className="h-3.5 w-3.5 text-blue-700" />
                          )}
                        </button>
                      ) : (
                        <button
                          className={cn(
                            "inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-card",
                            invoiceSentToTrendyol ? "border-emerald-200 bg-emerald-50/40" : "border-border"
                          )}
                          onClick={() => {
                            handleOpenInvoice(o);
                          }}
                          title={isTrendyolOrder ? (invoiceSentToTrendyol ? "Fatura Trendyol'a gönderildi" : "Fatura Trendyol'a gönderilmedi") : "Faturayı aç"}
                        >
                          <FileText className={cn("h-3.5 w-3.5", invoiceSentToTrendyol ? "text-emerald-600" : "text-muted-foreground")} />
                        </button>
                      )}

                      <button
                        className={cn(
                          "inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-card",
                          hasPrintableLabel ? "border-emerald-200" : "border-border"
                        )}
                        onClick={() => (hasPrintableLabel ? openPrintSidebar([o.id]) : handleCreateShipment(o.id))}
                        disabled={shippingOrder === o.id || (!hasPrintableLabel && !canCreateCargo)}
                        title="Kargo"
                      >
                        {shippingOrder === o.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : (
                          <Package className={cn("h-3.5 w-3.5", hasPrintableLabel ? "text-emerald-600" : "text-muted-foreground")} />
                        )}
                      </button>

                      <button
                        className={cn(
                          "inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-card",
                          hasPrintableLabel ? "border-emerald-200" : "border-border"
                        )}
                        onClick={() => openPrintSidebar([o.id])}
                        disabled={!hasPrintableLabel}
                        title="Yazdır"
                      >
                        <Printer className={cn("h-3.5 w-3.5", hasPrintableLabel ? "text-emerald-600" : "text-slate-300")} />
                      </button>

                      <div className="relative">
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card"
                          onClick={() => setOpenMenuId((prev) => (prev === o.id ? null : o.id))}
                          title="Daha Fazla"
                        >
                          {sharingInvoiceId === o.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </button>
                        {openMenuId === o.id && (
                          <div className="absolute right-0 top-9 z-20 min-w-[190px] rounded-lg border border-border bg-card p-1 shadow-lg">
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleInvoice(o);
                              }}
                              disabled={hasCreatedInvoice || invoicing === o.id || ["cancelled", "deleted"].includes(o.status)}
                            >
                              {invoicing === o.id ? "Oluşturuluyor..." : "Fatura Oluştur"}
                            </button>
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleOpenInvoice(o);
                              }}
                              disabled={!canOpenInvoice}
                            >
                              Faturayı Aç
                            </button>
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => handleShareInvoiceToStore(o)}
                              disabled={!(isTrendyolOrder || isHepsiburadaOrder || isPazaramaOrder || isIdefixOrder) || !o.parasut_invoice_id || sharingInvoiceId === o.id}
                            >
                              {sharingInvoiceId === o.id ? "İletiliyor..." : "Faturayı Mağazaya İlet"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <div className="min-w-[920px] lg:min-w-0">
          {/* Table Header */}
          <div className="grid grid-cols-[40px_minmax(90px,0.9fr)_minmax(160px,1.6fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(200px,1.6fr)_minmax(160px,1.4fr)] gap-0 border-b border-border bg-[#f7fafc] px-4 sm:px-6 lg:px-8 py-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.07em]" data-testid="orders-table-header">
            <div className="flex items-center">
              <Checkbox
                checked={allChecked}
                onCheckedChange={toggleAll}
                data-testid="select-all-checkbox"
              />
            </div>
            <div className="flex items-center">Sipariş No</div>
            <div className="flex items-center">Müşteri</div>
            <div className="flex items-center justify-end pr-4">Sipariş Tutarı</div>
            <div className="flex items-center justify-center">Sipariş Tarihi</div>
            <div className="flex items-center">Durum</div>
            <div className="flex items-center justify-end pr-2"></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-border bg-card" data-testid="orders-table-body">
            {loading ? (
              <div className="px-4 py-20 text-center">
                <Loader2 className="h-7 w-7 mx-auto animate-spin text-slate-300" />
                <p className="mt-3 text-sm text-muted-foreground">Siparişler yükleniyor...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <Package className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-muted-foreground">Sipariş bulunamadı.</p>
                <p className="text-xs text-muted-foreground mt-1">Filtreleri değiştirin veya siparişleri çekin.</p>
              </div>
            ) : (
              orders.map((o) => {
                const isSelected = !!selected[o.id];
                const statusCfg = getOrderStatusConfig(o.status);
                const orderChannel = getOrderChannel(o, provider);
                const isTrendyolOrder = orderChannel === "trendyol";
                const isHepsiburadaOrder = orderChannel === "hepsiburada";
                const isPazaramaOrder = orderChannel === "pazarama";
                const isIdefixOrder = orderChannel === "idefix";
                const needsIdefixAccept = isIdefixOrder && ["shipment_ready", "created"].includes(String(o.idefix_status || "").toLowerCase());
                const hasPrintableLabel = hasPrintableCargoLabel(o, provider);
                const canCreateCargo = canTriggerCargoCreation(o, provider);
                const cargoTooltip = getCargoTooltip(o, provider);
                const printTooltip = isTrendyolOrder
                  ? "Trendyol etiketini yazdır"
                  : (isHepsiburadaOrder
                    ? "Hepsiburada etiketini yazdır"
                    : (orderChannel === "n11"
                      ? (hasPrintableLabel ? "N11 etiketini yazdır" : "Önce etiketi hazırla")
                      : (hasPrintableLabel ? "Etiket Yazdır" : "Önce kargo oluşturun")));
                const invoiceSentToTrendyol = isTrendyolOrder && Boolean(o.trendyol_invoice_shared);
                const hasCreatedInvoice = hasRealInvoice(o);
                const canOpenInvoice = Boolean(o.parasut_invoice_id || o.parasut_invoice_url);
                const isInvoiced = Boolean(
                  hasRealInvoice(o) ||
                  (isHepsiburadaOrder && o.hb_invoice_missing !== true)
                );
                const needsInvoice = isHepsiburadaOrder
                  ? (o.hb_invoice_missing === true && !["cancelled", "deleted"].includes(o.status))
                  : (!isInvoiced && !["cancelled", "deleted"].includes(o.status));
                const invoiceBadgeType = getInvoiceBadgeType(o);

                return (
                  <div
                    key={o.id}
                    className={cn(
                      "grid grid-cols-[40px_minmax(90px,0.9fr)_minmax(160px,1.6fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(200px,1.6fr)_minmax(160px,1.4fr)] gap-0 px-4 sm:px-6 lg:px-8 py-3 items-center transition-colors hover:bg-[#f2f8fc]",
                      isSelected && "bg-blue-50/40"
                    )}
                    data-testid={`order-row-${o.id}`}
                  >
                    {/* Checkbox */}
                    <div className="flex items-center">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => setSelected((prev) => ({ ...prev, [o.id]: checked }))}
                        data-testid={`order-checkbox-${o.id}`}
                      />
                    </div>

                    {/* Sipariş No */}
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="min-w-0">
                        <button
                          className="text-sm font-semibold text-slate-800 hover:text-blue-600 transition-colors cursor-pointer truncate"
                          onClick={() => setOrderDetailOpen(o)}
                          data-testid={`order-no-btn-${o.id}`}
                        >
                          {getDisplayOrderNo(o)}
                        </button>
                        {getDisplayPackageNo(o) ? (
                          <p className="truncate text-[11px] text-muted-foreground">{getDisplayPackageNo(o)}</p>
                        ) : null}
                      </div>
                      <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                      {o.items?.length > 0 && (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-border bg-muted/50" title="Sipariş notu">
                          <StickyNote className="h-3 w-3 text-muted-foreground" />
                        </span>
                      )}
                    </div>

                    {/* Müşteri */}
                    <div className="flex items-center gap-1 min-w-0">
                      <button
                        className="text-sm text-foreground/80 hover:text-violet-600 transition-colors cursor-pointer truncate"
                        onClick={() => setCustomerDetailOpen(o)}
                        data-testid={`customer-btn-${o.id}`}
                      >
                        {o.customer_name || "-"}
                      </button>
                      <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                    </div>

                    {/* Sipariş Tutarı */}
                    <div className="text-sm font-medium text-slate-800 text-right pr-4 tabular-nums">
                      {formatCurrency(o.total_amount)}
                    </div>

                    {/* Sipariş Tarihi */}
                    <div className="text-sm text-muted-foreground text-center">
                      {formatOrderDate(o.order_date || o.created_at)}
                    </div>

                    {/* Durum */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={cn(
                          "inline-flex h-7 min-w-[108px] items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium border whitespace-nowrap",
                          statusCfg.cls
                        )}
                        data-testid={`order-status-badge-${o.id}`}
                      >
                        {statusCfg.label}
                      </span>
                      <IconBadge
                        type={invoiceBadgeType}
                        icon={FileText}
                        tooltip={invoiceBadgeType === "success" ? "Faturalandı" : "Fatura bekliyor"}
                        testId={`invoice-badge-${o.id}`}
                      />
                      <IconBadge
                        type={getBarcodeBadgeType(o)}
                        icon={ScanBarcode}
                        tooltip={cargoTooltip}
                        testId={`barcode-badge-${o.id}`}
                      />
                      <IconBadge
                        type={getSlipBadgeType(o)}
                        icon={Printer}
                        tooltip={printTooltip}
                        testId={`print-badge-${o.id}`}
                      />
                    </div>

                    {/* İşlemler */}
                    <div className="flex items-center justify-end gap-1 pr-2 flex-nowrap">
                      {(isTrendyolProvider && String(o.trendyol_status || "").toLowerCase() === "created") || needsIdefixAccept ? (
                        <button
                          className="text-xs font-semibold text-amber-700 hover:text-amber-800 whitespace-nowrap transition-colors"
                          onClick={() => handleTakeInProcessing(o)}
                          disabled={takingProcessingId === o.id}
                          data-testid={`take-processing-btn-${o.id}`}
                        >
                          {takingProcessingId === o.id ? "..." : "İşleme Al"}
                        </button>
                      ) : null}

                      {isHepsiburadaOrder ? (
                        <button
                          className={cn(
                            "h-7 w-7 rounded-md border bg-card inline-flex items-center justify-center transition-colors",
                            canOpenInvoice ? "border-border hover:bg-muted/50" : "border-border opacity-40 cursor-not-allowed"
                          )}
                          onClick={() => canOpenInvoice && handleOpenInvoice(o)}
                          disabled={!canOpenInvoice}
                          data-testid={`invoice-link-${o.id}`}
                        >
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      ) : needsInvoice ? (
                        <button
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap transition-colors"
                          onClick={() => handleInvoice(o)}
                          disabled={invoicing === o.id}
                          data-testid={`invoice-btn-${o.id}`}
                        >
                          {invoicing === o.id ? "..." : "Faturalandır"}
                        </button>
                      ) : (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className={cn(
                                  "h-7 w-7 rounded-md border bg-card inline-flex items-center justify-center transition-colors",
                                  invoiceSentToTrendyol
                                    ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50/50"
                                    : "border-border hover:bg-muted/50"
                                )}
                                data-testid={`invoice-link-${o.id}`}
                                onClick={() => {
                                  handleOpenInvoice(o);
                                }}
                              >
                                <FileText className={cn("h-3.5 w-3.5", invoiceSentToTrendyol ? "text-emerald-600" : "text-muted-foreground")} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                {isTrendyolOrder
                                  ? (invoiceSentToTrendyol ? "Fatura Trendyol'a gönderildi" : "Fatura Trendyol'a gönderilmedi")
                                  : "Faturayı aç"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className={cn(
                                "h-7 w-7 rounded-md border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors",
                                hasPrintableLabel ? "border-emerald-200 text-emerald-600" : "border-border"
                              )}
                              onClick={() => hasPrintableLabel ? openPrintSidebar([o.id]) : handleCreateShipment(o.id)}
                              disabled={shippingOrder === o.id || (!hasPrintableLabel && !canCreateCargo)}
                              data-testid={`cargo-btn-${o.id}`}
                            >
                              {shippingOrder === o.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                : <Package className={cn("h-3.5 w-3.5", hasPrintableLabel ? "text-emerald-600" : "text-muted-foreground")} />
                              }
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">{cargoTooltip}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className={cn(
                                "h-7 w-7 rounded-md border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors",
                                hasPrintableLabel ? "border-emerald-200" : "border-border"
                              )}
                              onClick={() => openPrintSidebar([o.id])}
                              disabled={!hasPrintableLabel}
                              data-testid={`print-btn-${o.id}`}
                            >
                              <Printer className={cn("h-3.5 w-3.5", hasPrintableLabel ? "text-emerald-600" : "text-slate-300")} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">{printTooltip}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <div className="relative">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="h-7 w-7 rounded-md border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors"
                                data-testid={`more-btn-${o.id}`}
                                onClick={() => setOpenMenuId((prev) => (prev === o.id ? null : o.id))}
                              >
                                <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent><p className="text-xs">Daha fazla</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {openMenuId === o.id && (
                          <div className="absolute right-0 top-8 z-20 min-w-[190px] rounded-lg border border-border bg-card p-1 shadow-lg">
                            {isIdefixOrder && needsIdefixAccept ? (
                              <button
                                className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleTakeInProcessing(o);
                                }}
                                disabled={takingProcessingId === o.id}
                              >
                                {takingProcessingId === o.id ? "İşleniyor..." : "Siparişi Kabul Et"}
                              </button>
                            ) : null}
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleInvoice(o);
                              }}
                              disabled={hasCreatedInvoice || invoicing === o.id || ["cancelled", "deleted"].includes(o.status)}
                            >
                              {invoicing === o.id ? "Oluşturuluyor..." : "Fatura Oluştur"}
                            </button>
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleOpenInvoice(o);
                              }}
                              disabled={!canOpenInvoice}
                            >
                              Faturayı Aç
                            </button>
                            <button
                              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => handleShareInvoiceToStore(o)}
                              disabled={!(isTrendyolOrder || isHepsiburadaOrder || isPazaramaOrder || isIdefixOrder) || !o.parasut_invoice_id || sharingInvoiceId === o.id}
                            >
                              {sharingInvoiceId === o.id ? "İletiliyor..." : "Faturayı Mağazaya İlet"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
            </div>
          </div>
        </div>

        {/* ─── Pagination Footer ─── */}
        {!loading && orders.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-border bg-card px-4 sm:px-6 lg:px-8 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between" data-testid="pagination-footer">
            <div className="flex items-center gap-2">
              <span>
                Sipariş: {total === 0 ? 0 : (page - 1) * perPage + 1} - {Math.min(page * perPage, total)} / {total}
              </span>
              <span className="text-slate-300">|</span>
              <span>Sayfa: {page} / {totalPages}</span>
            </div>

            <div className="flex items-center gap-1" data-testid="pagination">
              <button
                className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                data-testid="page-prev"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              {pageNumbers.map((p) => (
                <button
                  key={p}
                  className={cn(
                    "h-8 min-w-[32px] rounded-lg border px-2 text-xs font-medium transition-colors",
                    p === page
                      ? "border-[#1487a3] bg-[#1487a3] text-white"
                      : "border-border bg-card text-muted-foreground hover:bg-muted/50"
                  )}
                  onClick={() => setPage(p)}
                  data-testid={`page-btn-${p}`}
                >
                  {p}
                </button>
              ))}

              {totalPages > 5 && page < totalPages - 2 && (
                <>
                  <span className="px-1 text-muted-foreground">...</span>
                  <button
                    className="h-8 min-w-[32px] rounded-lg border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-muted/50"
                    onClick={() => setPage(totalPages)}
                    data-testid={`page-btn-${totalPages}`}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                data-testid="page-next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modals ─── */}
      <OrderDetailModal
        order={orderDetailOpen}
        open={!!orderDetailOpen}
        onClose={() => setOrderDetailOpen(null)}
        onStatusChange={handleStatusChange}
        statusUpdating={statusUpdatingId === orderDetailOpen?.id}
        provider={integration?.provider}
      />
      <CustomerDetailModal
        order={customerDetailOpen}
        open={!!customerDetailOpen}
        onClose={() => setCustomerDetailOpen(null)}
      />

      {/* ─── Cargo Label Sidebar ─── */}
      {cargoSidebarOpen && (
        <CargoLabelSidebar
          open={cargoSidebarOpen}
          onClose={() => { setCargoSidebarOpen(false); setCargoSidebarOrders([]); }}
          selectedOrders={cargoSidebarOrders}
          orders={orders}
          onRefresh={fetchOrders}
          provider={integration?.provider}
        />
      )}
    </div>
  );
}
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const normalizeListRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.orders)) return payload.orders;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.result?.items)) return payload.result.items;
    if (Array.isArray(payload?.payload?.items)) return payload.payload.items;
    return [];
  };
  const normalizeTotal = (payload, fallbackLen = 0) =>
    Number(
      payload?.total ??
      payload?.count ??
      payload?.total_count ??
      payload?.data?.total ??
      payload?.data?.count ??
      payload?.data?.total_count ??
      payload?.meta?.total ??
      fallbackLen
    ) || 0;
  const normalizeTotalPages = (payload, fallbackTotal, fallbackPerPage) => {
    const raw = Number(
      payload?.total_pages ??
      payload?.pages ??
      payload?.data?.total_pages ??
      payload?.data?.pages ??
      payload?.meta?.total_pages ??
      0
    );
    if (raw > 0) return raw;
    return Math.max(1, Math.ceil((Number(fallbackTotal || 0) || 0) / (Number(fallbackPerPage || 20) || 20)));
  };
